import { useEffect, useRef, useCallback, useState } from 'react';
import axios from 'axios';
import { logError } from '../utils/logger';

/**
 * Hook que mantém uma conexão WebSocket com o backend e expõe:
 * - status: 'connecting' | 'open' | 'closed'
 * - subscribe(ticketId): inscreve para receber updates desse ticket
 * - unsubscribe(ticketId): cancela inscrição
 *
 * onMessage recebe cada mensagem do servidor com forma {type, ...payload}.
 *
 * Reconnect automático com backoff exponencial (max 30s).
 * Auto-detecta wss/ws baseado em REACT_APP_BACKEND_URL.
 */
export function useTicketWebSocket(enabled, onMessage) {
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const onMessageRef = useRef(onMessage);
  const [status, setStatus] = useState('closed');
  const enabledRef = useRef(enabled);

  // Keep onMessage ref current without re-creating socket
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Auth via cookie httpOnly (enviado automaticamente pelo navegador no handshake)
  const buildUrl = useCallback(() => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const wsBase = base.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
    return `${wsBase}/api/ws`;
  }, []);

  const connect = useCallback(() => {
    if (!enabledRef.current) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    try {
      setStatus('connecting');
      const ws = new WebSocket(buildUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
        setStatus('open');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          onMessageRef.current?.(data);
        } catch (e) {
          logError('useTicketWebSocket.parse', e);
        }
      };

      ws.onerror = (e) => {
        logError('useTicketWebSocket.error', e);
      };

      ws.onclose = async (event) => {
        setStatus('closed');
        wsRef.current = null;
        // Access token expirado no handshake → renova sessão antes de reconectar
        if (event?.code === 4401) {
          try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`, null, { withCredentials: true });
          } catch (e) {
            // sessão expirada de verdade — backoff continua, api.js cuidará do redirect
          }
        }
        // Backoff exponencial: 1s, 2s, 4s, 8s, 16s, max 30s
        const delay = Math.min(30000, 1000 * Math.pow(2, reconnectAttemptsRef.current));
        reconnectAttemptsRef.current += 1;
        reconnectTimerRef.current = setTimeout(connect, delay);
      };
    } catch (e) {
      logError('useTicketWebSocket.connect', e);
      setStatus('closed');
    }
  }, [buildUrl]);

  useEffect(() => {
    if (!enabled) return undefined;
    connect();
    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        // Remove onclose para evitar reconexão durante unmount
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [enabled, connect]);

  const send = useCallback((payload) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify(payload));
        return true;
      } catch (e) {
        logError('useTicketWebSocket.send', e);
      }
    }
    return false;
  }, []);

  const subscribe = useCallback((ticketId) => {
    return send({ type: 'subscribe_ticket', ticket_id: ticketId });
  }, [send]);

  const unsubscribe = useCallback((ticketId) => {
    return send({ type: 'unsubscribe_ticket', ticket_id: ticketId });
  }, [send]);

  return { status, subscribe, unsubscribe, send };
}
